import { queryClient } from "@verbleif/lib/src/composables/Query/queryClient";
import { releaseAccountSlot } from "@verbleif/shared/src/composables/AccountsStore/releaseAccountSlot";
import { ACTIVE_ACCOUNT_SESSION_KEY, AUTHUSER_QUERY_PARAM, useAccountsStore } from "@verbleif/shared/src/composables/AccountsStore/useAccountsStore";
import { revokeAccessToken } from "@verbleif/shared/src/composables/Api/Plugin/Auth/revokeAccessToken";
import { usePkce } from "@verbleif/shared/src/composables/Api/usePkce";
import { resetMyAppStoreAccess } from "@verbleif/shared/src/composables/AppStore/useMyAppStoreAccess";
import { revokeCurrentSession } from "@verbleif/shared/src/composables/AuthStore/revokeCurrentSession";
import { useAuthStore } from "@verbleif/shared/src/composables/AuthStore/useAuthStore";
import { useConfigStore } from "@verbleif/shared/src/composables/ConfigStore/useConfigStore";
import { useLocationStore } from "@verbleif/shared/src/composables/LocationStore/useLocationStore";
import { useSharedStore } from "@verbleif/shared/src/composables/SharedStore/useSharedStore";
import { useSystemStore } from "@verbleif/shared/src/composables/SystemStore/useSystemStore";
import { useDeviceState } from "@verbleif/shared/src/composables/useDeviceState";
import { deleteFirebaseToken } from "@verbleif/shared/src/composables/useFirebase";
import router from "@web/core/plugin/router";
import { clearPersistedQueryCache } from "@web/core/queryPersister";
import { clearPropertyStateMaps } from "@web/features/Tasks/TaskExtraData/propertyStateMaps";
import Gleap from "gleap";
import { rememberAccountSwitchToast } from "../Accounts/accountSwitchToast";
import { useImpersonationStore } from "../Impersonate/useImpersonationStore";

function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function useLogout() {
  const authStore = useAuthStore();
  const { config } = useConfigStore();
  const systemStore = useSystemStore();
  const locationStore = useLocationStore();
  const impersonationStore = useImpersonationStore();
  const sharedStore = useSharedStore();
  const { unregisterDevice } = useDeviceState();
  const accountsStore = useAccountsStore();
  const pkce = usePkce();

  async function releaseSession() {
    // All calls need the access token, so they run before the stores below drop it. The session
    // revoke goes before the access token revoke, which would otherwise reject its bearer.
    await unregisterDevice();
    await revokeCurrentSession();
    await revokeAccessToken();
  }

  async function logout({
    redirectToLogin = true,
    keepReturnUrl = false,
    logoutAuthServer = true,
    allAccounts = false,
    continueAsOtherAccount = true,
  }: {
    redirectToLogin?: boolean
    keepReturnUrl?: boolean
    logoutAuthServer?: boolean
    /** Releases every account slot of this browser, not only the one of this tab. */
    allAccounts?: boolean
    /** Keep working in this tab as the most recently used other account instead of going to the login screen. */
    continueAsOtherAccount?: boolean
  } = {}) {
    if (!config.value) {
      throw new Error("Config not set");
    }

    // Resolved before the slot goes: the other accounts are not affected by it, but the choice must not change mid-logout.
    const nextAccount = !allAccounts && continueAsOtherAccount ? accountsStore.nextAccount.value : null;
    const otherIndexes = allAccounts ? accountsStore.otherAccounts.value.map(account => account.index) : [];

    await router.push({ name: "logout" });

    // Runs while the logout screen shows.
    await Promise.all([
      redirectToLogin ? releaseSession() : Promise.resolve(),
      redirectToLogin ? Promise.all(otherIndexes.map(index => releaseAccountSlot(index))) : Promise.resolve(),
      delay(1000),
    ]);

    if (redirectToLogin) {
      console.log("[Logout] Resetting auth store,keepReturnUrl", keepReturnUrl);
      await Promise.all([
        authStore.reset(keepReturnUrl),
        // Only this tab's account leaves, unless the person signs out everywhere; other accounts keep their own refresh tokens.
        allAccounts ? accountsStore.removeAllAccounts() : accountsStore.removeAccount(accountsStore.activeIndex),
        locationStore.reset(),
        impersonationStore.reset(),
        sharedStore.reset(),
        queryClient.clear(),
        clearPropertyStateMaps(),
        resetMyAppStoreAccess(),
        // Cleanup only: a failed token deletion must never keep the user logged in.
        deleteFirebaseToken().catch((e) => {
          console.log("Failed to delete Firebase token", e);
        }),
      ]);
      try {
        Gleap.clearIdentity();
        Gleap.showFeedbackButton(false);
      } catch (e) {
        console.log("Failed to clear Gleap identity", e);
      }

      clearPersistedQueryCache();

      if (nextAccount && logoutAuthServer && import.meta.env.VITE_SKIP_OAUTH !== "true") {
        // The login-host session may belong to the user who just left, so it ends too. The auth server sends the
        // browser back to the web app, which opens on the slot this tab was just re-targeted to.
        prepareSwitch(nextAccount.index);
        window.location.replace(authServerLogoutUrl(config.value));
      } else if (nextAccount) {
        switchAccount(nextAccount.index);
      } else if (import.meta.env.VITE_SKIP_OAUTH === "true") {
        await router.push({ name: "login" }).then(() => {
          systemStore.reset();
        });
      } else {
        if (logoutAuthServer) {
          window.location.replace(`${config.value.loginUrl}/logout`);
        } else {
          window.location.replace(config.value.loginUrl);
        }
      }
    }

    systemStore.reset();
  }

  // Drops this tab's session-bound state (tokens, selected location) without touching any account
  // slot, so the reload picks up the target slot instead of refreshing the account it came from.
  function retargetTab(index: number) {
    sessionStorage.setItem(ACTIVE_ACCOUNT_SESSION_KEY, String(index));
    authStore.token.value = null;
    authStore.refreshToken.value = null;
    authStore.refreshTokenRemembered.value = null;
    locationStore.reset();
    resetMyAppStoreAccess();
  }

  // The auth server only redirects to a logout URI registered for the client, compared as the exact string.
  function authServerLogoutUrl(appConfig: { loginUrl: string, loginClientId: string, loginRedirect: string }): string {
    const query = new URLSearchParams({ client_id: appConfig.loginClientId, redirect_uri: `${appConfig.loginRedirect}/` });
    return `${appConfig.loginUrl}/logout?${query.toString()}`;
  }

  function prepareSwitch(index: number) {
    const target = accountsStore.accounts.value.find(account => account.index === index);
    if (target) {
      rememberAccountSwitchToast({ name: target.name, locationName: target.locationName ?? null });
    }
    retargetTab(index);
  }

  function switchAccount(index: number) {
    prepareSwitch(index);
    window.location.assign(`/?${AUTHUSER_QUERY_PARAM}=${index}`);
  }

  function addAccount() {
    retargetTab(accountsStore.nextFreeIndex());
    // `prompt=login` skips the "Doorgaan als" chooser of the remembered login-host session, so the e-mail step shows
    // directly. Other accounts in this browser keep working on their own refresh tokens.
    window.location.assign(pkce.generateAuthorizationUrl(undefined, { prompt: "login", addAccount: true }));
  }

  // Signs in again to a slot whose session expired. The dead refresh token goes first, so the reload cannot try it.
  function reloginAccount(index: number) {
    const account = accountsStore.accounts.value.find(a => a.index === index);
    accountsStore.writeRefreshToken(null, index);
    retargetTab(index);
    window.location.assign(pkce.generateAuthorizationUrl(undefined, { prompt: "login", loginHint: account?.username }));
  }

  return { logout, switchAccount, addAccount, reloginAccount };
}
